import { LogOutModal } from "@/components/log-out-modal";
import { LoyaltyBadges } from "@/components/loyalty-badges";
import { SwitchMode } from "@/components/switch-mode";
import { ThemedText } from "@/components/themed-text";
import { Avatar } from "@/components/ui/avatar";
import { BrandColors } from "@/constants/theme";
import { useAuth } from "@/contexts/auth-context";
import { useNotifications } from "@/hooks/queries/use-notifications-query";
import { setAppColorScheme, useColorScheme } from "@/hooks/use-color-scheme";
import { useDesktopLayout, useServiceSearchQuery } from "@/hooks/use-desktop-layout";
import { setServiceSearchQuery } from "@/lib/desktop-layout";
import { getRoleLabel } from "@/lib/roles";
import Ionicons from "@react-native-vector-icons/ionicons";
import { router, usePathname } from "expo-router";
import React, { useState } from "react";
import {
  Image,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";

type NavIcon = React.ComponentProps<typeof Ionicons>["name"];

const CUSTOMER_NAV: { label: string; href: string; icon: NavIcon }[] = [
  { label: "Home", href: "/", icon: "home" },
  { label: "Bookings", href: "/bookings", icon: "calendar" },
  { label: "Payments", href: "/payment", icon: "card" },
  { label: "Notifications", href: "/notifications", icon: "notifications" },
  { label: "Help Center", href: "/help-center", icon: "help-circle" },
  { label: "Invite a Friend", href: "/referral", icon: "people" },
];

const DRIVER_NAV: { label: string; href: string; icon: NavIcon }[] = [
  { label: "Home", href: "/", icon: "home" },
  { label: "Bookings", href: "/bookings", icon: "calendar" },
  { label: "My Vehicles", href: "/vehicles", icon: "car-sport" },
  { label: "Wallet", href: "/wallet", icon: "wallet" },
  { label: "Notifications", href: "/notifications", icon: "notifications" },
  { label: "Help Center", href: "/help-center", icon: "help-circle" },
  { label: "Invite a Friend", href: "/referral", icon: "people" },
];

export function DesktopLogo() {
  const { theme } = useAuth();

  return (
    <View style={styles.logoRow}>
      <Image source={require("@/assets/images/icon.png")} style={styles.logo} />
      <Text style={[styles.logoWord, { color: theme.text }]}>AUTO</Text>
      <Text style={[styles.logoWord, { color: BrandColors.primary }]}>TRANSPORTER</Text>
      <Text style={styles.logoUk}>UK</Text>
    </View>
  );
}

export function DriverSidebarProfile() {
  const { user, theme } = useAuth();
  const fullName = `${user?.first_name ?? ""} ${user?.last_name ?? ""}`.trim();
  const roleColor = user?.role === "driver" ? "#10b981" : "#3b82f6";

  return (
    <TouchableOpacity
      style={[styles.driverCard, { borderColor: theme.border }]}
      onPress={() => router.push("/(tabs)/profile")}
      accessibilityRole="button"
      accessibilityLabel="Profile"
    >
      <Avatar src={user?.avatar} name={fullName} size={44} />
      <View style={styles.driverMeta}>
        <ThemedText style={styles.driverName} numberOfLines={1}>
          {fullName}
        </ThemedText>
        <View style={[styles.roleBadge, { backgroundColor: roleColor }]}>
          <ThemedText style={styles.roleText}>{getRoleLabel(user?.role)}</ThemedText>
        </View>
        <View style={styles.ratingRow}>
          <Ionicons name="star" size={12} color="#facc15" />
          <ThemedText style={styles.ratingValue}>
            {user?.reputation_score} rating
            {user?.total_ratings_count === 1 ? "" : "s"}
          </ThemedText>
        </View>
        {user?.loyalty_tier || user?.is_top_rated ? (
          <View style={styles.achievementBadges}>
            <LoyaltyBadges
              loyaltyTier={user?.loyalty_tier}
              isTopRated={user?.is_top_rated}
            />
          </View>
        ) : null}
      </View>
    </TouchableOpacity>
  );
}

export function DesktopTopBar() {
  const { user, theme } = useAuth();
  const colorScheme = useColorScheme();
  const isDark = colorScheme === "dark";
  const pathname = usePathname();
  const query = useServiceSearchQuery();
  const { data } = useNotifications(1);
  const hasUnread = (data?.data?.data ?? []).some((item) => !item.read_at);
  const firstName = user?.first_name?.trim() || "Account";
  const controlColor = isDark ? "#1B2436" : BrandColors.backgroundGray;

  const onSearch = (text: string) => {
    setServiceSearchQuery(text);
    if (text.trim().length > 0 && pathname !== "/") {
      router.push("/");
    }
  };

  return (
    <View
      style={[
        styles.topBar,
        { backgroundColor: theme.background, borderBottomColor: theme.border },
      ]}
    >
      <View
        style={[
          styles.search,
          { backgroundColor: controlColor, borderColor: theme.border },
        ]}
      >
        <Ionicons name="search" size={16} color={theme.icon} />
        <TextInput
          value={query}
          onChangeText={onSearch}
          placeholder="Search services..."
          placeholderTextColor={BrandColors.mediumGray}
          style={[styles.searchInput, { color: theme.text }]}
          testID="desktop_service_search"
          accessibilityLabel="Search services"
        />
      </View>
      <View style={styles.topActions}>
        <TouchableOpacity
          onPress={() => setAppColorScheme(isDark ? "light" : "dark")}
          accessibilityRole="button"
          accessibilityLabel={isDark ? "Switch to light theme" : "Switch to dark theme"}
          style={[styles.roundButton, { backgroundColor: controlColor }]}
          testID="desktop_theme_toggle"
        >
          <Ionicons
            name={isDark ? "sunny-outline" : "moon-outline"}
            size={20}
            color={theme.text}
          />
        </TouchableOpacity>
        <TouchableOpacity
          onPress={() => router.push("/(tabs)/notifications")}
          accessibilityRole="button"
          accessibilityLabel="Notifications"
          style={[styles.roundButton, { backgroundColor: controlColor }]}
        >
          <Ionicons name="notifications-outline" size={20} color={theme.text} />
          {hasUnread ? <View style={styles.unreadDot} /> : null}
        </TouchableOpacity>
        <TouchableOpacity
          onPress={() => router.push("/(tabs)/profile")}
          style={styles.userChip}
          accessibilityRole="button"
          accessibilityLabel="Profile"
        >
          <Avatar name={firstName} src={user?.avatar} size={38} />
          <ThemedText style={styles.userName}>{firstName}</ThemedText>
          <Ionicons name="chevron-forward" size={16} color={theme.icon} />
        </TouchableOpacity>
      </View>
    </View>
  );
}

function DesktopSideNav() {
  const { user, theme, signOut } = useAuth();
  const pathname = usePathname();
  const isDriver = user?.role === "driver";
  const items = isDriver ? DRIVER_NAV : CUSTOMER_NAV;
  const [logoutModalVisible, setLogoutModalVisible] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const handleConfirmLogout = async () => {
    setLoggingOut(true);
    try {
      await signOut();
      setLogoutModalVisible(false);
    } catch (error) {
      console.error("Error signing out:", error);
    } finally {
      setLoggingOut(false);
    }
  };

  return (
    <View
      style={[
        styles.sideNav,
        { backgroundColor: theme.background, borderRightColor: theme.border },
      ]}
    >
      <DesktopLogo />
      {isDriver ? <DriverSidebarProfile /> : null}
      <View style={styles.navList}>
        {items.map((item) => {
          const active = pathname === item.href;
          return (
            <Pressable
              key={item.href}
              onPress={() => router.push(item.href as never)}
              style={[
                styles.navItem,
                active ? { backgroundColor: "rgba(15, 185, 177, 0.12)" } : null,
              ]}
            >
              <Ionicons
                name={item.icon}
                size={20}
                color={active ? BrandColors.primary : theme.icon}
              />
              <Text
                style={{
                  color: active ? BrandColors.primary : theme.text,
                  fontSize: 16,
                  fontWeight: active ? "600" : "400",
                  flex: 1,
                }}
              >
                {item.label}
              </Text>
              {item.href === "/" && active ? <View style={styles.homeDot} /> : null}
            </Pressable>
          );
        })}
      </View>
      <View style={{ flex: 1 }} />
      <Pressable
        onPress={() => setLogoutModalVisible(true)}
        style={styles.navItem}
      >
        <Ionicons name="log-out-outline" size={20} color={BrandColors.error} />
        <Text style={{ color: BrandColors.error, fontSize: 16 }}>Log Out</Text>
      </Pressable>
      <SwitchMode />
      <LogOutModal
        visible={logoutModalVisible}
        onCancel={() => {
          if (!loggingOut) {
            setLogoutModalVisible(false);
          }
        }}
        onConfirm={handleConfirmLogout}
        loading={loggingOut}
      />
    </View>
  );
}

export function DesktopAppFrame({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const desktop = useDesktopLayout();
  const isDriver = user?.role === "driver";
  const canShow = isDriver ? user && user?.driver?.is_verified && (user?.vehicles_count || 0) > 0 : user;

  if (!desktop) {
    return <>{children}</>;
  }

  return (
    <View style={styles.frame}>
      {canShow ? <DesktopSideNav /> : null}
      <View style={styles.frameMain}>
        {canShow ? <DesktopTopBar /> : null}
        <View style={styles.frameBody}>{children}</View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  frame: {
    flex: 1,
    flexDirection: "row",
  },
  frameMain: {
    flex: 1,
  },
  frameBody: {
    flex: 1,
  },
  sideNav: {
    width: 340,
    borderRightWidth: 1,
    paddingBottom: 16,
  },
  logoRow: {
    flexDirection: "row",
    alignItems: "flex-end",
    gap: 4,
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 16,
  },
  logo: {
    width: 28,
    height: 28,
    borderRadius: 8,
    marginRight: 4,
  },
  logoWord: {
    fontSize: 14,
    fontWeight: "700",
    letterSpacing: 0.3,
  },
  logoUk: {
    fontSize: 9,
    fontWeight: "700",
    color: BrandColors.primary,
    marginBottom: 1,
  },
  driverCard: {
    marginHorizontal: 12,
    marginBottom: 12,
    padding: 12,
    borderRadius: 16,
    borderWidth: 1,
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  driverMeta: {
    flex: 1,
    gap: 4,
  },
  driverName: {
    fontSize: 14,
    fontWeight: "700",
  },
  roleBadge: {
    alignSelf: "flex-start",
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 12,
  },
  roleText: {
    fontSize: 11,
    fontWeight: "700",
    color: "#fff",
  },
  ratingRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  ratingValue: {
    fontSize: 12,
    fontWeight: "600",
  },
  achievementBadges: {
    marginTop: 6,
  },
  navList: {
    gap: 2,
    paddingHorizontal: 8,
  },
  navItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 12,
    marginHorizontal: 8,
  },
  homeDot: {
    width: 7,
    height: 7,
    borderRadius: 4,
    backgroundColor: BrandColors.success,
  },
  topBar: {
    minHeight: 72,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    paddingHorizontal: 32,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  search: {
    flex: 1,
    maxWidth: 540,
    height: 44,
    borderRadius: 22,
    borderWidth: 1,
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 16,
  },
  searchInput: {
    flex: 1,
    fontSize: 14,
    paddingVertical: 0,
  },
  topActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  roundButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: "center",
    justifyContent: "center",
  },
  unreadDot: {
    position: "absolute",
    top: 10,
    right: 12,
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: BrandColors.error,
  },
  userChip: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginLeft: 4,
  },
  userName: {
    fontSize: 15,
    fontWeight: "600",
  },
});
